import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, NavLink, Routes, Route, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import {
  LayoutDashboard, Users, LogOut, Shield,
  Building2, Bed, Home, IndianRupee, TrendingUp, Pause, Play, Loader2, Search,
  Inbox, MessageCircle, ShieldCheck, Wallet, CreditCard, MessageSquare
} from 'lucide-react';
import AdminDemoRequests from './AdminDemoRequests';
import KollyTestChat from './KollyTestChat';
import AdminOnboardings from './AdminOnboardings';
import AdminWalletTopups from './AdminWalletTopups';
import AdminPayments from './AdminPayments';
import AdminInbound from './AdminInbound';
import AdminKollyChats from './AdminKollyChats';
import PageMeta from '../PageMeta';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// --- Shared auth hook ---
const useAdminAuth = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');
  const user = React.useMemo(
    () => JSON.parse(localStorage.getItem('admin_user') || 'null'),
    []
  );
  const logout = React.useCallback(() => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    navigate('/admin/login');
  }, [navigate]);
  const authHeaders = React.useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );
  useEffect(() => {
    if (!token || user?.role !== 'super_admin') navigate('/admin/login');
  }, [token, user, navigate]);
  return { token, user, logout, authHeaders };
};

// ================= DASHBOARD =================
const AdminDashboard = () => {
  const { authHeaders } = useAdminAuth();
  const [stats, setStats]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [excludeTest, setExcludeTest] = useState(() => {
    try { return localStorage.getItem('admin_exclude_test') !== 'false'; } catch { return true; }
  });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const sRes = await axios.get(`${API}/admin/stats`, { headers: authHeaders, params: { exclude_test: excludeTest } });
      setStats(sRes.data);
    } catch (e) {
      toast({ title: 'Failed to load', variant: 'destructive' });
    } finally { setLoading(false); }
  }, [authHeaders, excludeTest]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggleExcludeTest = () => {
    const v = !excludeTest;
    setExcludeTest(v);
    try { localStorage.setItem('admin_exclude_test', String(v)); } catch { /* ignore */ }
  };

  if (loading) return <div className="p-8"><Loader2 className="animate-spin" /></div>;
  if (!stats) return null;

  const kpis = [
    { label: 'Total Clients',    value: stats.clients.real ?? stats.clients.total, sub: `${stats.clients.total - (stats.clients.real ?? stats.clients.total)} test accounts`, color: 'from-blue-500 to-indigo-600',   icon: Users },
    { label: 'Properties',       value: stats.inventory.properties,    sub: `${stats.inventory.rooms} rooms`,       color: 'from-purple-500 to-indigo-600', icon: Building2 },
    { label: 'Total Beds',       value: stats.inventory.beds,          sub: `${stats.inventory.occupancy_pct}% occ`, color: 'from-teal-500 to-emerald-600',  icon: Bed },
    { label: 'Tenants',          value: stats.inventory.tenants,       sub: 'onboarded',                            color: 'from-rose-500 to-pink-600',     icon: Home },
    { label: 'Actual MRR',       value: `₹${(stats.revenue.mrr_actual/1000).toFixed(1)}k`, sub: `₹${(stats.revenue.mrr_potential/100000).toFixed(1)}L potential`, color: 'from-green-500 to-emerald-600', icon: IndianRupee },
  ];

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Platform Overview</h1>
          <p className="text-gray-500 text-sm mt-1">Real-time snapshot of all client accounts, inventory, and revenue.</p>
        </div>
        {/* Exclude-test toggle */}
        <label
          data-testid="admin-exclude-test-toggle"
          className="inline-flex items-center gap-2 bg-white border border-gray-200 rounded-full px-3 py-2 cursor-pointer select-none shadow-sm hover:shadow transition-shadow"
        >
          <input
            type="checkbox"
            checked={excludeTest}
            onChange={toggleExcludeTest}
            className="w-4 h-4 accent-emerald-600 cursor-pointer"
          />
          <span className="text-xs font-semibold text-gray-700">
            Hide test data
          </span>
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${excludeTest ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
            {excludeTest ? 'ON · real clients only' : 'OFF · showing everything'}
          </span>
        </label>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {kpis.map((k, i) => {
          const Icon = k.icon;
          return (
            <div key={i} data-testid={`admin-kpi-${k.label.replace(/\s+/g, '-').toLowerCase()}`} className={`bg-gradient-to-br ${k.color} rounded-2xl p-4 text-white shadow-lg`}>
              <div className="flex items-center justify-between">
                <Icon size={20} className="text-white/80" />
                <TrendingUp size={12} className="text-white/60" />
              </div>
              <p className="text-2xl font-bold mt-3">{k.value}</p>
              <p className="text-xs font-medium text-white/80">{k.label}</p>
              <p className="text-[10px] text-white/60 mt-0.5">{k.sub}</p>
            </div>
          );
        })}
      </div>

      {/* WhatsApp Signups — Kolly Pilot Pulse */}
      {stats.whatsapp_signups && (
        <div
          data-testid="admin-whatsapp-signups-card"
          className="rounded-2xl p-5 text-white shadow-lg bg-gradient-to-br from-[#25D366] via-[#128C7E] to-[#075E54] relative overflow-hidden"
        >
          <div className="absolute -right-6 -top-6 opacity-10 text-[140px] leading-none select-none">💬</div>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-full px-3 py-1 text-[11px] font-semibold tracking-wide uppercase">
                <span className="w-2 h-2 rounded-full bg-white animate-pulse" /> Kolly pilot
              </div>
              <h2 className="text-xl font-bold mt-2">WhatsApp-native signups</h2>
              <p className="text-white/70 text-xs">Owners joining directly via +91 98219 22296 — no website required.</p>
            </div>
            <NavLink
              to="/admin/clients?source=whatsapp"
              data-testid="admin-whatsapp-see-clients"
              className="text-xs bg-white/15 hover:bg-white/25 transition-colors rounded-full px-3 py-1.5 font-semibold"
            >
              See owners →
            </NavLink>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-4">
            {[
              { label: 'Today',       value: stats.whatsapp_signups.today,       k: 'today' },
              { label: 'This week',   value: stats.whatsapp_signups.this_week,   k: 'week' },
              { label: 'This month',  value: stats.whatsapp_signups.this_month,  k: 'month' },
              { label: 'Active',      value: stats.whatsapp_signups.active,      k: 'active', sub: 'has ≥1 property' },
              { label: 'In progress', value: stats.whatsapp_signups.in_progress, k: 'wip',    sub: 'mid-signup' },
            ].map((b) => (
              <div
                key={b.k}
                data-testid={`admin-wa-stat-${b.k}`}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/10"
              >
                <p className="text-[10px] uppercase tracking-wide text-white/60 font-semibold">{b.label}</p>
                <p className="text-2xl font-bold mt-0.5">{b.value ?? 0}</p>
                {b.sub && <p className="text-[10px] text-white/50 mt-0.5">{b.sub}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Welcome-message engagement */}
      {stats.welcome && (
        <div
          data-testid="admin-welcome-card"
          className="rounded-2xl p-5 text-white shadow-lg bg-gradient-to-br from-indigo-600 via-purple-700 to-pink-700 relative overflow-hidden"
        >
          <div className="absolute -right-6 -top-6 opacity-10 text-[140px] leading-none select-none">👋</div>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-full px-3 py-1 text-[11px] font-semibold tracking-wide uppercase">
                <span className="w-2 h-2 rounded-full bg-white animate-pulse" /> Founder welcome
              </div>
              <h2 className="text-xl font-bold mt-2">New-owner welcome engagement</h2>
              <p className="text-white/70 text-xs">Sent automatically on first OTP login. Replies land in Kolly Chat History.</p>
            </div>
            <NavLink
              to="/admin/kolly-chats"
              data-testid="admin-welcome-see-replies"
              className="text-xs bg-white/15 hover:bg-white/25 transition-colors rounded-full px-3 py-1.5 font-semibold"
            >
              See replies →
            </NavLink>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-4">
            {[
              { label: 'Sent total',       value: stats.welcome.sent_total,    k: 'sent-total' },
              { label: 'Replied total',    value: stats.welcome.replied_total, k: 'replied-total' },
              { label: 'Sent (7d)',        value: stats.welcome.sent_week,     k: 'sent-week' },
              { label: 'Replied (7d)',     value: stats.welcome.replied_week,  k: 'replied-week' },
              { label: 'Reply rate',       value: `${stats.welcome.reply_rate ?? 0}%`, k: 'reply-rate' },
            ].map((b) => (
              <div
                key={b.k}
                data-testid={`admin-welcome-stat-${b.k}`}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/10"
              >
                <p className="text-[10px] uppercase tracking-wide text-white/60 font-semibold">{b.label}</p>
                <p className="text-2xl font-bold mt-0.5">{b.value ?? 0}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pending Signups Preview — REMOVED: access requests are no longer collected. */}
    </div>
  );
};

// ================= CLIENTS =================
const AdminClients = () => {
  const { authHeaders } = useAdminAuth();
  const location = useLocation();
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [hideTest, setHideTest] = useState(true);
  const [sourceFilter, setSourceFilter] = useState(() => {
    const sp = new URLSearchParams(window.location.search);
    return sp.get('source') || 'all'; // all | whatsapp | web
  });
  const [loading, setLoading] = useState(true);
  const [actingOn, setActingOn] = useState(null);

  // Sync filter when query string changes (from dashboard "See owners →" link)
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const s = sp.get('source');
    if (s && s !== sourceFilter) setSourceFilter(s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/clients`, { headers: authHeaders });
      setClients(res.data);
    } catch (e) { toast({ title: 'Failed', variant: 'destructive' }); }
    finally { setLoading(false); }
  }, [authHeaders]);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const suspend = async (c) => {
    if (!window.confirm(`Suspend ${c.name} (${c.phone})? They will be blocked from logging in.`)) return;
    setActingOn(c.id);
    try {
      await axios.post(`${API}/admin/clients/${c.id}/suspend`, {}, { headers: authHeaders });
      toast({ title: 'Suspended', description: c.name });
      fetchClients();
    } catch (e) { toast({ title: 'Failed', variant: 'destructive' }); }
    finally { setActingOn(null); }
  };

  const activate = async (c) => {
    setActingOn(c.id);
    try {
      await axios.post(`${API}/admin/clients/${c.id}/activate`, {}, { headers: authHeaders });
      toast({ title: '✅ Activated', description: c.name });
      fetchClients();
    } catch (e) { toast({ title: 'Failed', variant: 'destructive' }); }
    finally { setActingOn(null); }
  };

  const filtered = clients.filter(c => {
    if (hideTest && c.is_test) return false;
    if (sourceFilter === 'whatsapp' && c.signup_source !== 'whatsapp') return false;
    if (sourceFilter === 'web' && c.signup_source === 'whatsapp') return false;
    const s = search.toLowerCase();
    return !s || c.name?.toLowerCase().includes(s) || c.phone?.includes(s) || c.signup_property_name?.toLowerCase().includes(s);
  });
  const testCount = clients.filter(c => c.is_test).length;
  const realCount = clients.length - testCount;
  const waCount = clients.filter(c => c.signup_source === 'whatsapp' && !c.is_test).length;

  return (
    <div className="space-y-4" data-testid="admin-clients-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Clients</h1>
          <p className="text-gray-500 text-sm mt-1">
            All PG/Hostel/Apartment operators on the platform.
            <span className="ml-2 text-green-700 font-medium">{realCount} real</span>
            {waCount > 0 && <span className="ml-2 text-[#128C7E] font-medium">· {waCount} via WhatsApp</span>}
            {testCount > 0 && <span className="ml-2 text-orange-600 font-medium">· {testCount} test</span>}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Source filter pills */}
          <div className="inline-flex bg-gray-100 rounded-full p-1" data-testid="client-source-filter">
            {[
              { k: 'all',      label: 'All' },
              { k: 'whatsapp', label: '💬 WhatsApp' },
              { k: 'web',      label: '🌐 Web' },
            ].map(opt => (
              <button
                key={opt.k}
                data-testid={`client-source-${opt.k}`}
                onClick={() => setSourceFilter(opt.k)}
                className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors ${
                  sourceFilter === opt.k
                    ? (opt.k === 'whatsapp' ? 'bg-[#25D366] text-white shadow' : 'bg-white text-gray-900 shadow')
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >{opt.label}</button>
            ))}
          </div>
          <label className="flex items-center gap-2 text-xs font-medium text-gray-700 cursor-pointer select-none" data-testid="hide-test-toggle-wrap">
            <input
              type="checkbox"
              data-testid="hide-test-toggle"
              checked={hideTest}
              onChange={(e) => setHideTest(e.target.checked)}
              className="w-4 h-4 accent-amber-500"
            />
            Hide test accounts
          </label>
          <div className="relative w-64">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              data-testid="clients-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, phone, property…"
              className="pl-9 pr-3 py-2 w-full border border-gray-300 rounded-lg text-sm"
            />
          </div>
        </div>
      </div>

      <Card className="overflow-hidden">
        {loading ? <div className="p-8 text-center"><Loader2 className="animate-spin mx-auto" /></div>
        : filtered.length === 0 ? <div className="p-12 text-center text-gray-400">No clients match.</div>
        : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="clients-table">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Client</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Phone</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Property</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Inventory</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Tenants</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Joined</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map(c => {
                  const suspended = c.status === 'suspended';
                  return (
                    <tr key={c.id} className="hover:bg-gray-50" data-testid={`client-row-${c.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white ${c.is_test ? 'bg-gradient-to-br from-amber-500 to-orange-600' : 'bg-gradient-to-br from-indigo-500 to-purple-600'}`}>
                            {c.is_test ? '🧪' : (c.name?.charAt(0) || '?')}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900 flex items-center gap-1.5">
                              {c.name}
                              {c.is_test && (
                                <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 font-bold tracking-wide" data-testid={`test-badge-${c.id}`}>
                                  TEST
                                </span>
                              )}
                              {c.signup_source === 'whatsapp' && (
                                <span
                                  data-testid={`wa-badge-${c.id}`}
                                  className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#25D366]/15 text-[#075E54] font-bold tracking-wide inline-flex items-center gap-0.5"
                                  title="Onboarded via WhatsApp"
                                >
                                  💬 WA
                                </span>
                              )}
                              {c.welcome_replied_at && (
                                <span
                                  data-testid={`welcome-replied-badge-${c.id}`}
                                  className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-bold tracking-wide inline-flex items-center gap-0.5"
                                  title={c.welcome_reply_text ? `Replied: ${c.welcome_reply_text}` : 'Replied to welcome message'}
                                >
                                  ✅ Replied
                                </span>
                              )}
                              {c.welcome_sent_at && !c.welcome_replied_at && (
                                <span
                                  data-testid={`welcome-sent-badge-${c.id}`}
                                  className="text-[9px] px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 font-semibold tracking-wide"
                                  title="Welcome message delivered, awaiting reply"
                                >
                                  👋 Welcomed
                                </span>
                              )}
                            </p>
                            <p className="text-[11px] text-gray-500">{c.role || 'manager'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 font-mono text-gray-700">{c.phone}</td>
                      <td className="px-4 py-3">
                        <p className="font-medium">{c.signup_property_name || '—'}</p>
                        {c.signup_property_type && <p className="text-[11px] text-gray-500">{c.signup_property_type}</p>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 text-xs text-gray-600">
                          <span>{c.stats.properties} props</span>
                          <span>·</span>
                          <span>{c.stats.rooms} rooms</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 font-semibold text-blue-700">{c.stats.tenants}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${suspended ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                          {suspended ? 'Suspended' : 'Active'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">{c.created_at ? new Date(c.created_at).toLocaleDateString() : '—'}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {suspended ? (
                            <Button size="sm" onClick={() => activate(c)} disabled={actingOn === c.id} className="bg-green-600 hover:bg-green-700 text-xs" data-testid={`activate-${c.id}`}>
                              {actingOn === c.id ? <Loader2 className="animate-spin" size={12} /> : <><Play size={12} className="mr-1" /> Activate</>}
                            </Button>
                          ) : (
                            <Button size="sm" variant="outline" onClick={() => suspend(c)} disabled={actingOn === c.id} className="text-red-600 border-red-200 hover:bg-red-50 text-xs" data-testid={`suspend-${c.id}`}>
                              {actingOn === c.id ? <Loader2 className="animate-spin" size={12} /> : <><Pause size={12} className="mr-1" /> Suspend</>}
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

// ================= LAYOUT =================
const ADMIN_TITLES = {
  dashboard:       ['Platform Overview',       'Real-time KPIs, WhatsApp pilot pulse and platform inventory.'],
  'demo-requests': ['Demo Requests',           'Lead CRM — incoming demo requests from the landing page.'],
  'wallet-topups': ['Wallet Top-Ups',           'Review and approve client wallet top-up requests.'],
  payments:        ['Payment Gateway',          'Toggle which payment gateway handles wallet top-ups.'],
  inbound:         ['Inbound WhatsApp',         'Everyone who messaged your RentKollect business number recently.'],
  'kolly-chats':   ['Kolly Chat History',       'Every conversation on your WhatsApp business number — full transcripts.'],
  kolly:           ['Kolly Test Chat',         'Simulate WhatsApp conversations with Kolly AI.'],
  onboardings:     ['Tenant Verifications',    'KYC uploads from tenant self-onboarding links.'],
  clients:         ['Clients',                 'All PG/hostel/apartment owners on the platform.'],
};

const AdminLayout = () => {
  const { user, logout } = useAdminAuth();
  const location = useLocation();

  const navItems = [
    { path: '/admin/dashboard',      icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin/demo-requests',  icon: Inbox,           label: 'Demo Requests' },
    { path: '/admin/kolly',          icon: MessageCircle,   label: 'Kolly (WhatsApp)' },
    { path: '/admin/onboardings',    icon: ShieldCheck,     label: 'Tenant Verifications' },
    { path: '/admin/wallet-topups',  icon: Wallet,          label: 'Wallet Top-Ups' },
    { path: '/admin/payments',       icon: CreditCard,      label: 'Payment Gateway' },
    { path: '/admin/inbound',        icon: MessageSquare,   label: 'Inbound WhatsApp' },
    { path: '/admin/kolly-chats',    icon: MessageCircle,   label: 'Kolly Chat History' },
    { path: '/admin/clients',        icon: Users,           label: 'Clients' },
  ];

  const segment = location.pathname.replace(/^\/admin\/?/, '').split('/')[0] || 'dashboard';
  const [pageTitle, pageDescription] = ADMIN_TITLES[segment] || ADMIN_TITLES.dashboard;

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <PageMeta title={`${pageTitle} — Super Admin`} description={pageDescription} noindex />
      {/* Sidebar */}
      <aside className="w-60 bg-gradient-to-b from-slate-900 to-indigo-950 text-white flex flex-col" data-testid="admin-sidebar">
        <div className="p-5 border-b border-white/10">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
              <Shield size={18} className="text-white" />
            </div>
            <div>
              <h1 className="font-bold">RentKollect</h1>
              <p className="text-[10px] text-amber-300 tracking-widest">SUPER ADMIN</p>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {navItems.map(item => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={({ isActive }) => `flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all
                  ${isActive ? 'bg-amber-500/20 text-amber-300 border-l-2 border-amber-400' : 'text-white/70 hover:bg-white/5 hover:text-white'}`}
              >
                <Icon size={16} />
                {item.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-3 border-t border-white/10">
          <div className="px-3 py-2 mb-2">
            <p className="text-xs text-white/60">Signed in as</p>
            <p className="text-sm font-semibold truncate">{user?.name}</p>
            <p className="text-[11px] text-white/50 font-mono">{user?.phone}</p>
          </div>
          <Button onClick={logout} variant="outline" className="w-full bg-white/5 border-white/10 text-white hover:bg-white/10 text-xs" data-testid="admin-logout">
            <LogOut size={12} className="mr-1" /> Log out
          </Button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-auto">
        <div className="p-8">
          <Routes>
            <Route path="dashboard"     element={<AdminDashboard />} />
            <Route path="demo-requests" element={<AdminDemoRequests />} />
            <Route path="kolly"         element={<KollyTestChat />} />
            <Route path="onboardings"   element={<AdminOnboardings />} />
            <Route path="wallet-topups" element={<AdminWalletTopups />} />
            <Route path="payments"      element={<AdminPayments />} />
            <Route path="inbound"       element={<AdminInbound />} />
            <Route path="kolly-chats"   element={<AdminKollyChats />} />
            <Route path="clients"       element={<AdminClients />} />
            <Route path="*"             element={<AdminDashboard />} />
          </Routes>
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
